import { axiosGet, axiosPost } from "../../apis/useAxios";

const airlineCode = () => sessionStorage.getItem("session_airlineCode");
const userName = () => sessionStorage.getItem("session_userName") ?? "";

const listOrThrow = (response, fallback) => {
  if (response && response.isSuccess === false && !/no record/i.test(response.message ?? "")) {
    throw new Error(response.message || fallback);
  }
  return response?.payload ?? [];
};

/** POST ActivityLocation/list: locations, optionally of one airport. */
export async function getActivityLocations(airportCode = "", options) {
  const response = await axiosPost(
    "ActivityLocation/list",
    { AirlineCode: airlineCode(), AirportCode: airportCode ?? "" },
    options,
  );
  return listOrThrow(response, "Unable to load activity locations.");
}

/** One location (with its mapped airports) by code; null when not found. */
export async function getActivityLocation(locationCode) {
  const response = await axiosPost("ActivityLocation/list", {
    AirlineCode: airlineCode(),
    LocationCode: locationCode,
  });
  return listOrThrow(response, "Unable to load the activity location.")[0] ?? null;
}

/** GET Airport/get-airport-dropdown/{airline}: the airline's airports. */
export async function getAirportDropdown() {
  const response = await axiosGet(`Airport/get-airport-dropdown/${airlineCode()}`);
  return listOrThrow(response, "Unable to load airports.");
}

const airportKey = (code) =>
  String(code ?? "")
    .trim()
    .toUpperCase();

/**
 * Airport dropdown of the add/edit form: options (mapped first), the selected codes and the skip flags.
 * Codes are trimmed / upper-cased on both sides so the list keys match the location's airports; a mapped
 * airport missing from the airline's airports is still listed, so it shows and is not dropped on save.
 */
export function toAirportSelection(options, location) {
  const selected = [...new Set((location?.mappedAirportCodes ?? []).map(airportKey).filter(Boolean))];
  const skip = {};
  (location?.mappedAirports ?? []).forEach((a) => {
    skip[airportKey(a.airportCode)] = a.skipTransportOnFlightArrivalDuty === true;
  });
  const byCode = new Map();
  (options ?? []).forEach((o) => {
    const code = airportKey(o.airportCode);
    if (code && !byCode.has(code))
      byCode.set(code, { ...o, airportCode: code, isMapped: selected.includes(code) ? 1 : 0 });
  });
  selected.forEach((code) => {
    if (!byCode.has(code)) byCode.set(code, { airportCode: code, airportName: code, isMapped: 1 });
  });
  const list = [...byCode.values()].sort((a, b) => b.isMapped - a.isMapped);
  return { options: list, selected, skip };
}

/** POST ActivityLocation/{id}/save (id 0 = add). */
export function saveActivityLocation(id, form, airports) {
  return axiosPost(`ActivityLocation/${id}/save`, {
    AirlineCode: airlineCode(),
    Airports: airports.map((a) => ({ AirportCode: a.airportCode, SkipTransportOnFlightArrivalDuty: a.skip === true })),
    LocationCode: form.locationCode,
    AddressTitle: form.addressTitle,
    Address: form.address,
    Landmark: form.landmark,
    City: form.city,
    State: form.state,
    Country: form.country,
    PostalCode: form.postalCode,
    Latitude: form.latitude,
    Longitude: form.longitude,
    UserName: userName(),
  });
}

/** Local-vendor rows of a location: one per mapped airport x crew type (P cockpit, F cabin). */
export async function getVendorSettings(locationCode) {
  const response = await axiosGet("AirlineActivityLocationLocalVendorMapping/settings", {
    airlineCode: airlineCode(),
    locationCode,
  });
  return listOrThrow(response, "Unable to load local vendors.");
}

/**
 * GET AirlineActivityLocationLocalVendorMapping/vendor-options: the local vendors mapped to each airport (TransporterMaster), one call for
 * all airports, grouped as { BLR: [{ id, name, isActive, isNonFlyReady, message }], ... } by name.
 * - isActive false (Transporter.IsActive) = deactivated: shown, but cannot be newly assigned.
 * - isNonFlyReady false = no service location / non-flying duty service: cannot be assigned (message says why).
 */
export async function getVendorOptions(airportCodes) {
  const codes = airportCodes ?? [];
  const byAirport = Object.fromEntries(codes.map((code) => [code, []]));
  if (!codes.length) return byAirport;
  const response = await axiosGet("AirlineActivityLocationLocalVendorMapping/vendor-options", { airportCodes: codes.join(",") });
  listOrThrow(response, "Unable to load local vendors.").forEach((v) => {
    const list = byAirport[v.airportCode] ?? (byAirport[v.airportCode] = []);
    if (list.some((o) => o.id === v.id)) return;
    list.push({
      id: v.id,
      name: v.name,
      isActive: v.isActive === true,
      isNonFlyReady: v.isNonFlyReady === true,
      message: v.message ?? "",
      // Flying duty (Airport › Fly duty vendors): service location + FLY customer.
      isFlyReady: v.isFlyReady === true,
      flyMessage: v.flyMessage ?? "",
    });
  });
  Object.values(byAirport).forEach((list) => list.sort((a, b) => String(a.name).localeCompare(String(b.name))));
  return byAirport;
}

/**
 * POST AirlineActivityLocationLocalVendorMapping/delete-latest: Delete latest — the most recent (scheduled) vendor row of one
 * airport x crew type of the location (row = the loaded row, latestRowIndex = that vendor row).
 */
export function deleteLatestVendor(locationCode, row) {
  return axiosPost("AirlineActivityLocationLocalVendorMapping/delete-latest", {
    AirlineCode: airlineCode(),
    LocationCode: locationCode,
    AirportCode: row.airportCode,
    CrewType: row.crewType,
    RowIndex: row.latestRowIndex,
    UserName: userName(),
  });
}

/**
 * GET AirlineActivityLocationLocalVendorMapping/history: change history of one airport x crew type of the location, newest first
 * ({ historyId, actionType, actionReason, wasStarted, actionBy, actionDate (UTC ISO), transporterName, wefDate, remarks }).
 */
export async function getVendorHistory(locationCode, airportCode, crewType) {
  const response = await axiosGet("AirlineActivityLocationLocalVendorMapping/history", {
    airlineCode: airlineCode(),
    locationCode,
    airportCode,
    crewType,
  });
  return listOrThrow(response, "Unable to load the local vendor history.");
}

/** POST AirlineActivityLocationLocalVendorMapping/save: the changed rows (a vendor is never cleared here). */
export function saveVendorSettings(rows) {
  return axiosPost("AirlineActivityLocationLocalVendorMapping/save", {
    ActivityLocationMapping: rows.map((r) => ({
      AirlineCode: airlineCode(),
      AirportCode: r.airportCode ?? "",
      LocationCode: r.locationCode ?? "",
      CrewType: r.crewType ?? "",
      TransporterId: r.transporterId ?? 0,
      IsMapped: r.isMapped === true,
      WefDate: r.wefDate ?? "",
      UserName: userName(),
    })),
  });
}

export const CREW_TYPE_LABELS = { P: "Cockpit", F: "Cabin" };

/** What SkipTransportOnFlightArrivalDuty means for the operator (shown where the flag is read). */
export const NO_TRANSPORT_ON_ARRIVAL_HINT =
  "No transport on arrival: this duty is inside the airport, so crew who land here by flight are not given transport to it.";

/** Shown where the flag is set: a wrong tick leaves crew without transport. */
export const NO_TRANSPORT_ON_ARRIVAL_WARNING =
  "Tick an airport only if this location is inside that airport and crew never go out of the airport for this duty. " +
  "When ticked, no transport is booked for crew who land at that airport by flight for this duty. " +
  "If you are not sure, leave it unticked.";

/**
 * GET ActivityLocation/hotel-mapping: the Set hotel tab rows, one per hotel x crew type (P, F):
 * { hotelCode, hotelName, airportCodes, isHotelActive, crewType, isMapped, isProvidingTransfer, isDefault }.
 * Hotels of the location's airports are listed unmapped so they can be mapped.
 */
export async function getLocationHotelMapping(locationCode) {
  const response = await axiosGet("ActivityLocation/hotel-mapping", {
    airlineCode: airlineCode(),
    locationCode,
  });
  return listOrThrow(response, "Unable to load the hotels of the location.");
}

/**
 * GET ActivityLocation/hotel-options: the hotel dropdown — hotels of one of the location's airports
 * (AirlineAirportHotelMapping): [{ hotelCode, hotelName, airportCodes, isHotelActive }].
 */
export async function getLocationHotelOptions(locationCode, airportCode) {
  const response = await axiosGet("ActivityLocation/hotel-options", {
    airlineCode: airlineCode(),
    locationCode,
    airportCode,
  });
  return listOrThrow(response, "Unable to load the hotels of the airport.");
}

/** POST ActivityLocation/save-hotel-mapping: the changed hotel x crew type rows of the location. */
export function saveLocationHotelMapping(locationCode, rows) {
  return axiosPost("ActivityLocation/save-hotel-mapping", {
    AirlineCode: airlineCode(),
    LocationCode: locationCode,
    Items: rows.map((r) => ({
      HotelCode: r.hotelCode,
      CrewType: r.crewType,
      IsMapped: r.isMapped === true,
      IsProvidingTransfer: r.isProvidingTransfer === true,
      IsDefault: r.isDefault === true,
    })),
    UserName: userName(),
  });
}

const coords = (l) => (l.latitude || l.longitude ? `${l.latitude ?? ""}, ${l.longitude ?? ""}` : "");

// Label / value pairs of the slider's Details tab.
export const LOCATION_FIELDS = [
  { label: "Location Code", value: (l) => l.locationCode },
  { label: "Address Title", value: (l) => l.addressTitle },
  { label: "Address", value: (l) => l.address },
  { label: "Landmark", value: (l) => l.landmark },
  { label: "City", value: (l) => l.city },
  { label: "State", value: (l) => l.state },
  { label: "Country", value: (l) => l.country },
  { label: "Postal Code", value: (l) => l.postalCode },
  { label: "Latitude, Longitude", value: coords },
  { label: "Modified By", value: (l) => l.modifiedByUser },
];

/** Label / value rows for one location, skipping empty values. */
export function toLocationRows(location) {
  if (!location) return [];
  return LOCATION_FIELDS.map((f) => ({ label: f.label, value: f.value(location) })).filter(
    (r) => r.value !== null && r.value !== undefined && String(r.value).trim() !== "",
  );
}

/**
 * GET ActivityLocation/hotel-history: change history of one hotel x crew type of the location, newest first
 * ({ historyId, actionType, actionReason, actionBy, actionDate (UTC ISO), isProvidingTransfer, isDefault, remarks }).
 */
export async function getHotelHistory(locationCode, hotelCode, crewType) {
  const response = await axiosGet("ActivityLocation/hotel-history", {
    airlineCode: airlineCode(),
    locationCode,
    hotelCode,
    crewType,
  });
  return listOrThrow(response, "Unable to load the hotel history.");
}

const filled = (value) => value !== null && value !== undefined && String(value).trim() !== "";

/** An India PIN code: exactly 6 digits (the activity location postal code). */
export const isPinCode = (value) => /^\d{6}$/.test(String(value ?? "").trim());

/**
 * The form after an address is accepted on the map (PlaceSearchComponent place:
 * { houseApartment, landmark, address, cityName, stateName, countryName, postalCode, latitude, longitude }):
 * - Address Title: kept when it already has a value; only an empty title is taken from the map (houseApartment);
 * - Address, Latitude, Longitude, Landmark, City, State, Country: from the map when it has a value, else kept as they were;
 * - Postal Code: from the map only when it is a 6-digit PIN code, else kept as it was.
 */
export function applyPlace(form, place) {
  const p = place ?? {};
  const keepOr = (current, next) => (filled(next) ? next : current);
  return {
    ...form,
    addressTitle: filled(form.addressTitle) ? form.addressTitle : (p.houseApartment ?? ""),
    address: keepOr(form.address, p.address),
    latitude: keepOr(form.latitude, p.latitude),
    longitude: keepOr(form.longitude, p.longitude),
    landmark: keepOr(form.landmark, p.landmark),
    city: keepOr(form.city, p.cityName),
    state: keepOr(form.state, p.stateName),
    country: keepOr(form.country, p.countryName),
    postalCode: isPinCode(p.postalCode) ? String(p.postalCode).trim() : form.postalCode,
  };
}
